import { transformValuesToString } from './utils';

// holds all native functions to access the SDK
Pollfish.mobile = (function mobile() {
  // object holding the reference to native implementation
  let handler = {
    panelClosed() {},
    panelOpened() {},
    pauseVideo() {},
    playVideo() {},
    exitFullScreen() {},
    webViewFocus() {},
    focusExplicitly() {},
  };

  // flag to mark survey completion
  let surveyCompletedFlag = false;

  // set survey completion flag
  const setSurveyCompletedFlag = (mode) => {
    surveyCompletedFlag = mode;
  };

  // get survey completion flag
  const isSurveyCompleted = () => surveyCompletedFlag;

  const ravenBreadcrumb = (category, data = {}) => {
    Raven.captureBreadcrumb({
      message: 'interface',
      category,
      data,
    });
  };

  return {
    interface: {
      // dependency injection for native handler
      init(implementation) {
        handler = implementation;
      },

      // mark a user as not eligible for the survey
      userNotEligible() {
        ravenBreadcrumb('userNotEligible');
        handler.userNotEligible();
      },

      // send the device data after user consent
      sentDataOfUserConsentToServer(url, shouldSendInstalledApps) {
        ravenBreadcrumb('sentDataOfUserConsentToServer', {
          url,
          shouldSendInstalledApps,
        });
        handler.sentDataOfUserConsentToServer(url, shouldSendInstalledApps);
      },

      // close panel and show indicator
      close() {
        handler.close();
        ravenBreadcrumb('close');
      },

      // close panel and don't show indicator
      closeAndNoShow() {
        handler.closeAndNoShow();
        ravenBreadcrumb('closeAndNoShow');
      },

      // inform SDK that the survey is finished
      setSurveyCompleted(participationStatusPayload) {
        ravenBreadcrumb('setSurveyCompleted');
        const payload = Pollfish.helper.getSurveyCompletedPayload(
          participationStatusPayload,
        );
        // check if survey is already completed prior
        // to mark as completed
        if (!isSurveyCompleted()) {
          // mark the private variable flag as completed
          setSurveyCompletedFlag(true);

          // notify event listeners about the event
          handler.setSurveyCompleted(payload);
        }
      },

      // present simple pop-up native message
      showToastMsg(msg) {
        ravenBreadcrumb('showToastMsg', { msg });
        handler.showToastMsg(msg);
      },

      // open website in native browser out of app
      openWebsite(url) {
        ravenBreadcrumb('openWebsite', { url });
        handler.openWebsite(url);
      },

      // SDK Webview only. Not currently used
      userRejectedSurvey() {
        ravenBreadcrumb('userRejectedSurvey');
        handler.userRejectedSurvey();
      },

      noSurveyFound() {
        ravenBreadcrumb('noSurveyFound');
        handler.noSurveyFound();
      },

      // notify SDK when everything is loaded
      webViewLoaded() {
        if (Pollfish.helper.webViewLoadedCalled) {
          return;
        }
        Pollfish.helper.webViewLoadedCalled = true;
        ravenBreadcrumb('webViewLoaded');
        handler.webViewLoaded();
      },

      // get data from server in json format
      getFromServer(callback) {
        ravenBreadcrumb('getFromServer');
        handler.getFromServer(async (serverResponse) => {
          Pollfish.helper.setupPollfishHelperJson(serverResponse);
          callback(JSON.stringify(Pollfish.helper.json));
        });
      },

      didGetFromServer(responseData) {
        ravenBreadcrumb('didGetFromServer');
        return handler.didGetFromServer(responseData);
      },

      // clear the queue cache
      clearCacheQueue() {
        ravenBreadcrumb('clearCacheQueue');
        handler.clearCacheQueue();
      },

      // send information to server
      sendToServer(url, data, saveAtQueue) {
        let json = data;
        // on an effort to make sendToServer calls contain only json
        // i will be keeping both possibilities for now and completely remove strings when ready
        if (typeof data === 'string') {
          try {
            json = JSON.parse(data);
          } catch (e) {
            Raven.captureException(
              'Error while trying to parse string json in send to server',
              {
                extra: Pollfish.helper.getErrorObject(),
              },
            );
          }
        }
        json.session_id = Pollfish.helper.json.session_id;
        if (!json.survey_session_id && Pollfish.survey) {
          json.survey_session_id = Pollfish.survey.getSurveySessionId();
        }
        if (Pollfish.survey) {
          json.language = Pollfish.survey.getSurveyLanguage();
        }

        json.apiType = Pollfish.helper.getApiType();

        // most top level values are expected to be strings
        // for the time being by the service and we should take that into account
        // ie: s_id, q_id, token etc
        const transformedJSON = transformValuesToString(json);
        const jsonString = JSON.stringify(transformedJSON);
        ravenBreadcrumb('sendToServer', { url, data, saveAtQueue });
        handler.sendToServer(url, jsonString, saveAtQueue);
      },

      // get device information to handle js and css compatibility
      getDeviceInfo(responseCallback) {
        ravenBreadcrumb('getDeviceInfo');
        return handler.getDeviceInfo(responseCallback);
      },

      didGetDeviceInfo(responseData) {
        ravenBreadcrumb('didGetDeviceInfo');
        return handler.didGetDeviceInfo(responseData);
      },

      // notify SDK that the focus is no the input
      textFieldFocus() {
        ravenBreadcrumb('textFieldFocus');
        handler.textFieldFocus();
      },

      // notify SDK that the focus is lost from input
      textFieldUnFocus() {
        ravenBreadcrumb('textFieldUnFocus');
        handler.textFieldUnFocus();
      },

      // notify SDK that the video has ended
      notifyVideoEnd() {
        ravenBreadcrumb('notifyVideoEnd');
        handler.notifyVideoEnd();
      },

      // interface for calls from SDK start

      // ios only focus on input of email for winner
      focusExplicitly(browserHeight) {
        ravenBreadcrumb('focusExplicitly', { browserHeight });
        handler.focusExplicitly(browserHeight);
      },

      // remove focus from input of email for winner
      loseFocus(emailInputFocus) {
        ravenBreadcrumb('loseFocus', { emailInputFocus });
        handler.loseFocus(emailInputFocus);
      },

      panelClosed() {
        ravenBreadcrumb('panelClosed');
        handler.panelClosed();
      },

      panelOpened() {
        ravenBreadcrumb('panelOpened');
        handler.panelOpened();
      },

      playVideo() {
        ravenBreadcrumb('playVideo');
        handler.playVideo();
      },

      pauseVideo() {
        ravenBreadcrumb('pauseVideo');
        handler.pauseVideo();
      },

      webViewFocus(mode) {
        ravenBreadcrumb('webViewFocus', { mode });
        handler.webViewFocus(mode);
      },

      // ios only function to exit full screen
      exitFullScreen(mode) {
        ravenBreadcrumb('exitFullScreen', { mode });
        handler.exitFullScreen(mode);
      },

      // interface for calls from SDK end
    },
  };
})();

export default Pollfish.mobile;
